import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Toolbar } from 'primereact/toolbar';
import { Toast } from 'primereact/toast';
import { KardexService } from '../../services/KardexService';
import { ProductoService } from '../../services/ProductoService';
import { useAuth } from '../../context/AuthContext';
import { SucursalService } from '../../services/SucursalService';
import PageHeader from '../../components/ui/PageHeader';

const Kardex = () => {
    const toast = useRef(null);
    const [loading, setLoading] = useState(false);
    const [movimientos, setMovimientos] = useState([]);
    const [varianteOptions, setVarianteOptions] = useState([]); // flattened for dropdown
    const [filtroVariante, setFiltroVariante] = useState(null);
    const [filtroTipo, setFiltroTipo] = useState(null);
    const [filtroSucursal, setFiltroSucursal] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    // Authorization Context for SupeAdmin multi-branch scenario
    const { user } = useAuth();
    const isMultiSucursal = !user?.idSucursal;
    const [sucursales, setSucursales] = useState([]);

    const [form, setForm] = useState({
        idVariante: null,
        idSucursal: null,
        tipo: 'entrada',
        cantidad: 1,
        motivo: '',
        observaciones: ''
    });

    const tipos = [
        { label: 'Entrada', value: 'entrada' },
        { label: 'Salida', value: 'salida' },
        { label: 'Ajuste', value: 'ajuste' },
        { label: 'Compra', value: 'compra' },
        { label: 'Merma', value: 'merma' }
    ];

    useEffect(() => {
        loadProductos();
        loadMovimientos();
        if (isMultiSucursal) {
            loadSucursales();
        }
    }, [isMultiSucursal]);

    const loadSucursales = async () => {
        try {
            const response = await SucursalService.getAll();
            setSucursales(response?.data || []);
        } catch (error) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'No se pudieron cargar sucursales', life: 3000 });
        }
    };

    const loadProductos = async () => {
        try {
            const response = await ProductoService.getAll();
            const prods = response?.data || [];
            // Flatten into variant options for dropdown
            const flat = [];
            prods.forEach(p => {
                if (p.variantes && p.variantes.length > 0) {
                    p.variantes.forEach(v => {
                        flat.push({ id: v.id, nombre: `${p.nombre} - ${v.valor}` });
                    });
                }
            });
            setVarianteOptions(flat);
        } catch (error) {
            toast.current.show({ severity: 'warn', summary: 'Atencion', detail: 'No se pudieron cargar productos', life: 3000 });
        }
    };

    const loadMovimientos = async (idVariante = null, idSucursal = null) => {
        setLoading(true);
        try {
            const response = idVariante ? await KardexService.getByVariante(idVariante, idSucursal) : await KardexService.getAll(idSucursal);
            setMovimientos(response?.data || []);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el kardex', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const movimientosFiltrados = useMemo(() => {
        return (movimientos || []).filter((m) => {
            const okTipo = filtroTipo ? m.tipo === filtroTipo : true;
            return okTipo;
        });
    }, [movimientos, filtroTipo]);

    const openNuevo = () => {
        setSubmitted(false);
        const defaultSucursal = isMultiSucursal && sucursales.length > 0 ? sucursales[0].id : null;
        setForm({ idVariante: null, idSucursal: defaultSucursal, tipo: 'entrada', cantidad: 1, motivo: '', observaciones: '' });
        setDialogVisible(true);
    };

    const saveMovimiento = async () => {
        setSubmitted(true);
        if (!form.idVariante || !form.tipo || !form.cantidad || form.cantidad <= 0 || (isMultiSucursal && !form.idSucursal)) {
            return;
        }

        try {
            await KardexService.create(form);
            toast.current.show({ severity: 'success', summary: 'Exito', detail: 'Movimiento registrado', life: 3000 });
            setDialogVisible(false);
            loadMovimientos(filtroVariante, filtroSucursal);
        } catch (error) {
            const detail = error?.response?.data?.message || 'No se pudo registrar el movimiento';
            toast.current.show({ severity: 'error', summary: 'Error', detail, life: 4000 });
        }
    };

    const fechaBody = (row) => {
        if (!row.fecha) return '-';
        return new Date(row.fecha).toLocaleString();
    };

    const tipoBody = (row) => {
        const tipo = (row.tipo || '').toUpperCase();
        const severity = ['SALIDA', 'MERMA'].includes(tipo) ? 'danger' : 'success';
        return <span className={`p-tag p-tag-${severity}`}>{tipo || '-'}</span>;
    };

    const toolbarLeft = () => (
        <div className="flex gap-2 align-items-center">
            <Button label="Nuevo Movimiento" icon="pi pi-plus" onClick={openNuevo} />
            <Dropdown
                value={filtroVariante}
                options={varianteOptions}
                onChange={(e) => {
                    setFiltroVariante(e.value);
                    loadMovimientos(e.value || null, filtroSucursal);
                }}
                optionLabel="nombre"
                optionValue="id"
                placeholder="Filtrar por producto"
                filter
                filterPlaceholder="Buscar producto..."
                showClear
                className="w-16rem"
            />
            {isMultiSucursal && (
                <Dropdown
                    value={filtroSucursal}
                    options={sucursales}
                    onChange={(e) => {
                        setFiltroSucursal(e.value);
                        loadMovimientos(filtroVariante, e.value || null);
                    }}
                    optionLabel="nombre"
                    optionValue="id"
                    placeholder="Filtrar por sucursal"
                    showClear
                    className="w-14rem"
                />
            )}
            <Dropdown
                value={filtroTipo}
                options={tipos}
                onChange={(e) => setFiltroTipo(e.value)}
                placeholder="Filtrar por tipo"
                showClear
                className="w-14rem"
            />
        </div>
    );

    const dialogFooter = (
        <div>
            <Button label="Cancelar" icon="pi pi-times" outlined onClick={() => setDialogVisible(false)} />
            <Button label="Guardar" icon="pi pi-check" onClick={saveMovimiento} />
        </div>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <PageHeader 
                    title="Kardex de Movimientos" 
                    icon="pi pi-history" 
                    description="Consulta y registra los movimientos de entrada y salida de inventario." 
                />
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="mb-3" left={toolbarLeft} />
                    <DataTable value={movimientosFiltrados} loading={loading} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} responsiveLayout="scroll"
                        emptyMessage="No hay movimientos registrados.">
                        <Column field="fecha" header="Fecha" body={fechaBody} sortable style={{ minWidth: '12rem' }} />
                        <Column field="nombreVariante" header="Producto / Variante" sortable style={{ minWidth: '12rem' }} />
                        <Column field="nombreSucursal" header="Sucursal" sortable style={{ minWidth: '10rem' }} />
                        <Column field="tipo" header="Tipo" body={tipoBody} sortable style={{ minWidth: '8rem' }} />
                        <Column field="cantidad" header="Cantidad" sortable style={{ minWidth: '8rem' }} />
                        <Column field="saldoAnterior" header="Saldo Ant." sortable style={{ minWidth: '8rem' }} />
                        <Column field="saldoActual" header="Saldo Act." sortable style={{ minWidth: '8rem' }} />
                        <Column field="motivo" header="Motivo" style={{ minWidth: '12rem' }} />
                        <Column field="nombreUsuario" header="Usuario" style={{ minWidth: '12rem' }} />
                    </DataTable>
                </div>
            </div>

            <Dialog visible={dialogVisible} style={{ width: '520px' }} header="Nuevo Movimiento" modal footer={dialogFooter} onHide={() => setDialogVisible(false)}>
                <div className="field mb-3">
                    <label htmlFor="idVariante">Producto / Variante</label>
                    <Dropdown
                        id="idVariante"
                        value={form.idVariante}
                        options={varianteOptions}
                        onChange={(e) => setForm((prev) => ({ ...prev, idVariante: e.value }))}
                        optionLabel="nombre"
                        optionValue="id"
                        placeholder="Buscar y seleccionar producto..."
                        filter
                        filterPlaceholder="Escribir nombre de producto..."
                        className={submitted && !form.idVariante ? 'p-invalid w-full' : 'w-full'}
                    />
                    {submitted && !form.idVariante && <small className="p-error">La variante es requerida.</small>}
                </div>
                <div className="field mb-3">
                    <label htmlFor="tipo">Tipo</label>
                    <Dropdown
                        id="tipo"
                        value={form.tipo}
                        options={tipos}
                        onChange={(e) => setForm((prev) => ({ ...prev, tipo: e.value }))}
                        className={submitted && !form.tipo ? 'p-invalid w-full' : 'w-full'}
                    />
                </div>
                <div className="field mb-3">
                    <label htmlFor="cantidad">Cantidad</label>
                    <InputNumber
                        id="cantidad"
                        value={form.cantidad}
                        min={1}
                        onValueChange={(e) => setForm((prev) => ({ ...prev, cantidad: e.value || 1 }))}
                        className={submitted && (!form.cantidad || form.cantidad <= 0) ? 'p-invalid w-full' : 'w-full'}
                    />
                </div>
                <div className="field mb-3">
                    <label htmlFor="motivo">Motivo</label>
                    <Dropdown
                        id="motivo"
                        value={form.motivo}
                        options={[
                            { label: 'Ajuste de inventario', value: 'ajuste' },
                            { label: 'Compra', value: 'compra' },
                            { label: 'Venta', value: 'venta' },
                            { label: 'Merma', value: 'merma' },
                            { label: 'Traspaso', value: 'traspaso' }
                        ]}
                        onChange={(e) => setForm((prev) => ({ ...prev, motivo: e.value }))}
                        placeholder="Seleccione un motivo"
                        className="w-full"
                    />
                </div>
                <div className="field">
                    <label htmlFor="observaciones">Observaciones</label>
                    <InputTextarea id="observaciones" rows={3} value={form.observaciones} onChange={(e) => setForm((prev) => ({ ...prev, observaciones: e.target.value }))} className="w-full" />
                </div>
            </Dialog>
        </div>
    );
};

export default Kardex;
